import * as React from 'react';
import { filterOptions } from './filterOptions';

/**
 * The search behaviour shared by every dropdown in the portal.
 *
 * Kept out of the component so the rules can be tested directly: what counts as
 * an option's text, when a search box is worth showing, and which options survive
 * what the user typed.
 */

/**
 * Below this many options a search box costs more than it gives - a two-item
 * "Approve / Reject" list does not want one, and on a phone it would pop the
 * keyboard for nothing.
 */
export const SEARCH_THRESHOLD = 8;

/** The visible text of an option, however deeply it is wrapped. */
export const textOf = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  if (React.isValidElement(node)) return textOf(node.props?.children);
  return '';
};

/**
 * Whether to offer a search box. An explicit `searchable` prop always wins, so a
 * caller can force one on a short list or suppress it on a long one.
 */
export const shouldShowSearch = (optionCount, override) =>
  override === undefined || override === null ? optionCount >= SEARCH_THRESHOLD : Boolean(override);

/**
 * Narrow a list of option elements by what was typed, preserving the portal-wide
 * ranking rule (names beginning with the query first, then those containing it).
 * Anything that is not an option - group labels, separators - drops out while a
 * search is active, since a heading with nothing under it only confuses.
 */
export const filterElements = (elements, query) => {
  if (!query || !query.trim()) return elements;
  const ranked = filterOptions(
    elements.map((element, i) => ({ value: i, label: textOf(element?.props?.children), element })),
    query,
    Number.MAX_SAFE_INTEGER
  );
  return ranked.shown.map((o) => o.element);
};

export default { textOf, shouldShowSearch, filterElements, SEARCH_THRESHOLD };
